import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardDescription, CardHeader } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { AlertCircle, CheckCircle2, Eye, EyeOff, XCircle, UserX, KeyRound } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const LOGO_URL = "https://customer-assets.emergentagent.com/job_agent-stats-1/artifacts/nm3zn43g_image.png";

function Login({ onLogin }) {
  const [isRegistering, setIsRegistering] = useState(false);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [name, setName] = useState('');
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [authError, setAuthError] = useState(null);
  
  // Validation states
  const [errors, setErrors] = useState({});
  const [touched, setTouched] = useState({});

  // Validation rules
  const validateEmail = (email) => {
    if (!email) return 'Email is required';
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) return 'Please enter a valid email address';
    return '';
  };

  const validatePassword = (password) => {
    if (!password) return 'Password is required';
    if (password.length < 6) return 'Password must be at least 6 characters';
    if (isRegistering) {
      if (!/[A-Z]/.test(password)) return 'Password must contain at least one uppercase letter';
      if (!/[a-z]/.test(password)) return 'Password must contain at least one lowercase letter';
      if (!/[0-9]/.test(password)) return 'Password must contain at least one number';
    }
    return '';
  };

  const validateName = (name) => {
    if (!name && isRegistering) return 'Name is required';
    if (name && name.length < 2) return 'Name must be at least 2 characters';
    return '';
  };

  // Run validation on field change
  useEffect(() => {
    const newErrors = {};
    if (touched.email) newErrors.email = validateEmail(email);
    if (touched.password) newErrors.password = validatePassword(password);
    if (touched.name && isRegistering) newErrors.name = validateName(name);
    setErrors(newErrors);
  }, [email, password, name, isRegistering, touched]);

  // Handle field blur (mark as touched)
  const handleBlur = (field) => {
    setTouched(prev => ({ ...prev, [field]: true }));
  };

  // Check if form is valid
  const isFormValid = () => {
    const emailError = validateEmail(email);
    const passwordError = validatePassword(password);
    const nameError = isRegistering ? validateName(name) : '';
    return !emailError && !passwordError && !nameError;
  };

  // Password strength indicator
  const getPasswordStrength = () => {
    if (!password) return { strength: 0, label: '', color: '' };
    let strength = 0;
    if (password.length >= 6) strength++;
    if (password.length >= 8) strength++;
    if (/[A-Z]/.test(password)) strength++;
    if (/[a-z]/.test(password)) strength++;
    if (/[0-9]/.test(password)) strength++;
    if (/[^A-Za-z0-9]/.test(password)) strength++;

    if (strength <= 2) return { strength: 33, label: 'Weak', color: 'bg-red-500' };
    if (strength <= 4) return { strength: 66, label: 'Medium', color: 'bg-yellow-500' };
    return { strength: 100, label: 'Strong', color: 'bg-green-500' };
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Clear previous auth error
    setAuthError(null);
    
    // Mark all fields as touched
    setTouched({ email: true, password: true, name: true });
    
    // Validate before submit
    if (!isFormValid()) {
      toast.error('Please fix the errors before submitting');
      return;
    }

    setLoading(true);

    try {
      const endpoint = isRegistering ? '/api/auth/register' : '/api/auth/login';
      const payload = isRegistering ? { email, password, name } : { email, password };
      
      const response = await axios.post(`${BACKEND_URL}${endpoint}`, payload);
      const { access_token } = response.data;
      
      onLogin(access_token);
      toast.success(isRegistering ? 'Account created successfully!' : 'Welcome back!');
    } catch (error) {
      const status = error.response?.status;
      const message = error.response?.data?.detail || 'Authentication failed';
      
      // Set auth error for inline display
      setAuthError({
        type: status === 404 ? 'not_found' : status === 401 ? 'wrong_password' : 'error',
        message: message
      });
      
      // Show toast with appropriate icon
      if (status === 404) {
        toast.error(message, {
          icon: <UserX className="h-5 w-5 text-red-500" />,
          duration: 5000,
        });
      } else if (status === 401) {
        toast.error(message, {
          icon: <KeyRound className="h-5 w-5 text-red-500" />,
          duration: 5000,
        });
      } else if (status === 400) {
        toast.error(message, {
          icon: <XCircle className="h-5 w-5 text-red-500" />,
          duration: 5000,
        });
      } else {
        toast.error(message);
      }
    } finally {
      setLoading(false);
    }
  };

  // Reset touched state when switching modes
  const handleModeSwitch = () => {
    setIsRegistering(!isRegistering);
    setTouched({});
    setErrors({});
    setAuthError(null);
  };

  const passwordStrength = getPasswordStrength();

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-50 to-slate-100">
      <Card className="w-full max-w-md shadow-xl" data-testid="login-card">
        <CardHeader className="space-y-4 flex flex-col items-center">
          <img 
            src={LOGO_URL} 
            alt="Synapse1 Logo" 
            className="h-16 w-auto"
            data-testid="login-logo"
          />
          <CardDescription className="text-base text-center">
            {isRegistering ? 'Create your account to get started' : 'Sign in to your account'}
          </CardDescription>
        </CardHeader>
        <CardContent>
          {/* Auth Error Alert */}
          {authError && (
            <div 
              className={`mb-4 p-4 rounded-lg border flex items-start gap-3 animate-in slide-in-from-top-2 ${
                authError.type === 'not_found' 
                  ? 'bg-orange-50 border-orange-200 text-orange-800' 
                  : 'bg-red-50 border-red-200 text-red-800'
              }`}
              data-testid="auth-error-alert"
            >
              {authError.type === 'not_found' ? (
                <UserX className="h-5 w-5 mt-0.5 flex-shrink-0" />
              ) : authError.type === 'wrong_password' ? (
                <KeyRound className="h-5 w-5 mt-0.5 flex-shrink-0" />
              ) : (
                <XCircle className="h-5 w-5 mt-0.5 flex-shrink-0" />
              )}
              <div className="flex-1">
                <p className="font-medium text-sm">{authError.message}</p>
                {authError.type === 'not_found' && (
                  <button 
                    type="button"
                    onClick={handleModeSwitch}
                    className="text-sm underline mt-1 hover:opacity-80"
                  >
                    Create a new account
                  </button>
                )}
              </div>
              <button 
                type="button" 
                onClick={() => setAuthError(null)}
                className="hover:opacity-70"
              >
                <XCircle className="h-4 w-4" />
              </button>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-4">
            {isRegistering && (
              <div className="space-y-2">
                <Label htmlFor="name" className="flex items-center justify-between">
                  Name
                  {touched.name && !errors.name && name && (
                    <CheckCircle2 className="h-4 w-4 text-green-500" />
                  )}
                </Label>
                <div className="relative">
                  <Input
                    id="name"
                    type="text"
                    placeholder="John Doe"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    onBlur={() => handleBlur('name')}
                    className={`${errors.name && touched.name ? 'border-red-500 focus-visible:ring-red-500' : ''} ${!errors.name && touched.name && name ? 'border-green-500 focus-visible:ring-green-500' : ''}`}
                    data-testid="name-input"
                  />
                </div>
                {errors.name && touched.name && (
                  <div className="flex items-center gap-1 text-red-500 text-sm animate-in slide-in-from-top-1" data-testid="name-error">
                    <AlertCircle className="h-4 w-4" />
                    <span>{errors.name}</span>
                  </div>
                )}
              </div>
            )}

            <div className="space-y-2">
              <Label htmlFor="email" className="flex items-center justify-between">
                Email
                {touched.email && !errors.email && email && (
                  <CheckCircle2 className="h-4 w-4 text-green-500" />
                )}
              </Label>
              <div className="relative">
                <Input
                  id="email"
                  type="email"
                  placeholder="you@example.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  onBlur={() => handleBlur('email')}
                  className={`${errors.email && touched.email ? 'border-red-500 focus-visible:ring-red-500' : ''} ${!errors.email && touched.email && email ? 'border-green-500 focus-visible:ring-green-500' : ''}`}
                  data-testid="email-input"
                />
              </div>
              {errors.email && touched.email && (
                <div className="flex items-center gap-1 text-red-500 text-sm animate-in slide-in-from-top-1" data-testid="email-error">
                  <AlertCircle className="h-4 w-4" />
                  <span>{errors.email}</span>
                </div>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="password" className="flex items-center justify-between">
                Password
                {touched.password && !errors.password && password && (
                  <CheckCircle2 className="h-4 w-4 text-green-500" />
                )}
              </Label>
              <div className="relative">
                <Input
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  placeholder="••••••••"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  onBlur={() => handleBlur('password')}
                  className={`pr-10 ${errors.password && touched.password ? 'border-red-500 focus-visible:ring-red-500' : ''} ${!errors.password && touched.password && password ? 'border-green-500 focus-visible:ring-green-500' : ''}`}
                  data-testid="password-input"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 transition-colors"
                  data-testid="toggle-password-visibility"
                >
                  {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
              {errors.password && touched.password && (
                <div className="flex items-center gap-1 text-red-500 text-sm animate-in slide-in-from-top-1" data-testid="password-error">
                  <AlertCircle className="h-4 w-4" />
                  <span>{errors.password}</span>
                </div>
              )}
              
              {/* Password strength indicator for registration */}
              {isRegistering && password && (
                <div className="space-y-1 animate-in fade-in" data-testid="password-strength">
                  <div className="flex items-center justify-between text-xs">
                    <span className="text-slate-500">Password strength</span>
                    <span className={`font-medium ${passwordStrength.color.replace('bg-', 'text-')}`}>
                      {passwordStrength.label}
                    </span>
                  </div>
                  <div className="h-1.5 w-full bg-slate-200 rounded-full overflow-hidden">
                    <div
                      className={`h-full ${passwordStrength.color} transition-all duration-300`}
                      style={{ width: `${passwordStrength.strength}%` }}
                    />
                  </div>
                  <ul className="text-xs text-slate-500 space-y-0.5 mt-2">
                    <li className={`flex items-center gap-1 ${password.length >= 6 ? 'text-green-600' : ''}`}>
                      {password.length >= 6 ? <CheckCircle2 className="h-3 w-3" /> : <span className="h-3 w-3 rounded-full border border-slate-300 inline-block" />}
                      At least 6 characters
                    </li>
                    <li className={`flex items-center gap-1 ${/[A-Z]/.test(password) ? 'text-green-600' : ''}`}>
                      {/[A-Z]/.test(password) ? <CheckCircle2 className="h-3 w-3" /> : <span className="h-3 w-3 rounded-full border border-slate-300 inline-block" />}
                      One uppercase letter
                    </li>
                    <li className={`flex items-center gap-1 ${/[a-z]/.test(password) ? 'text-green-600' : ''}`}>
                      {/[a-z]/.test(password) ? <CheckCircle2 className="h-3 w-3" /> : <span className="h-3 w-3 rounded-full border border-slate-300 inline-block" />}
                      One lowercase letter
                    </li>
                    <li className={`flex items-center gap-1 ${/[0-9]/.test(password) ? 'text-green-600' : ''}`}>
                      {/[0-9]/.test(password) ? <CheckCircle2 className="h-3 w-3" /> : <span className="h-3 w-3 rounded-full border border-slate-300 inline-block" />}
                      One number
                    </li>
                  </ul>
                </div>
              )}
            </div>

            <Button
              type="submit"
              className="w-full"
              disabled={loading || (Object.keys(touched).length > 0 && !isFormValid())}
              data-testid="submit-button"
            >
              {loading ? 'Please wait...' : isRegistering ? 'Create Account' : 'Sign In'}
            </Button>
          </form>
          <div className="mt-4 text-center">
            <button
              type="button"
              onClick={handleModeSwitch}
              className="text-sm text-slate-600 hover:text-slate-900 underline"
              data-testid="toggle-auth-mode"
            >
              {isRegistering ? 'Already have an account? Sign in' : "Don't have an account? Register"}
            </button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

export default Login;
